import { ref, uploadBytes } from 'firebase/storage';
import { firebase } from '@/lib/firebase.js';
import type { FrameRecord } from '@erectus/shared';
import { db, gzip, loadFrames, packFrames } from './db.js';

/**
 * Keypoint upload — Phase 5, opt-in only (docs/DATA_AND_ML.md §1).
 *
 * Preconditions enforced here *and* in storage.rules: the user must be 18+ and
 * have an active `dataOptIn`. Video is never part of this; the payload is the
 * quantized landmark time series plus the frame state.
 */
export interface UploadInput {
  uid: string;
  sessionId: string;
  sampleFps: number;
  dataOptIn: boolean;
}

function pathFor(uid: string, sessionId: string): string {
  return `sessions/${uid}/${sessionId}.bin`;
}

/** Upload one session's frames. Resolves 'empty' when nothing is left on the device; throws on failure. */
async function putKeypoints(uid: string, sessionId: string, sampleFps: number): Promise<'uploaded' | 'empty'> {
  const frames = await loadFrames(sessionId);
  if (frames.length === 0) return 'empty';
  const blob = await gzip(packFrames(frames, sampleFps));
  await uploadBytes(ref(firebase().storage, pathFor(uid, sessionId)), blob, {
    contentType: 'application/octet-stream',
    contentEncoding: 'gzip',
    customMetadata: { sampleFps: String(sampleFps), frames: String(frames.length) },
  });
  await db.sessions.update(sessionId, { uploadedAt: Date.now() });
  return 'uploaded';
}

async function queueRetry(uid: string, sessionId: string, sampleFps: number): Promise<void> {
  const queued = await db.outbox.where('kind').equals('keypoints').filter((item) => item.sessionId === sessionId).count();
  if (queued > 0) return;
  await db.outbox.add({
    kind: 'keypoints',
    sessionId,
    payload: { retryUpload: true, uid, sampleFps },
    createdAt: Date.now(),
    attempts: 0,
  });
}

export async function uploadKeypoints(input: UploadInput): Promise<string | null> {
  if (!input.dataOptIn) return null;
  try {
    return (await putKeypoints(input.uid, input.sessionId, input.sampleFps)) === 'uploaded'
      ? pathFor(input.uid, input.sessionId)
      : null;
  } catch {
    // Retried from the outbox on the next app start (finalize.flushOutbox), and
    // the frames are kept past the 7-day window until it goes. Never blocks the report.
    await queueRetry(input.uid, input.sessionId, input.sampleFps);
    return null;
  }
}

/** Outbox retry. 'failed' keeps the item; 'done' (uploaded, or no frames left to send) clears it. */
export async function retryKeypoints(sessionId: string, payload: unknown): Promise<'done' | 'failed'> {
  const { uid, sampleFps } = (payload ?? {}) as { uid?: string; sampleFps?: number };
  if (!uid) return 'done';
  try {
    await putKeypoints(uid, sessionId, sampleFps ?? 3);
    return 'done';
  } catch {
    return 'failed';
  }
}

/**
 * Turning sharing on also sends the sessions still on this phone — at most the
 * last DEVICE_FRAME_RETENTION_DAYS — that were never uploaded. The opt-in
 * notice says so (CONSENT_VERSIONS.dataOptIn 2026-10-07). Call only after the
 * consent is saved: the storage rule reads it. Returns how many went.
 */
export async function backfillKeypoints(uid: string): Promise<number> {
  const sessions = await db.sessions
    .where('uid')
    .equals(uid)
    .filter((s) => s.uploadedAt === null && s.status !== 'recording')
    .toArray();
  let sent = 0;
  for (const session of sessions) {
    try {
      if ((await putKeypoints(uid, session.id, session.sampleFps)) === 'uploaded') sent += 1;
    } catch {
      await queueRetry(uid, session.id, session.sampleFps);
    }
  }
  return sent;
}

/**
 * A posture check's frames (≈15–30), opt-in only, next to the session files so
 * the same Storage rule, deletion and withdrawal purge cover them. Returns the
 * path, or null when not shared or the upload failed (the check is kept either way).
 */
export async function uploadCheckFrames(input: {
  uid: string;
  checkId: string;
  frames: readonly FrameRecord[];
  sampleFps: number;
  dataOptIn: boolean;
}): Promise<string | null> {
  if (!input.dataOptIn || input.frames.length === 0) return null;
  const path = `sessions/${input.uid}/check-${input.checkId}.bin`;
  try {
    const blob = await gzip(packFrames(input.frames, input.sampleFps));
    await uploadBytes(ref(firebase().storage, path), blob, {
      contentType: 'application/octet-stream',
      contentEncoding: 'gzip',
      customMetadata: { sampleFps: String(input.sampleFps), frames: String(input.frames.length), kind: 'check' },
    });
    return path;
  } catch {
    return null;
  }
}
